import { gql, useQuery } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { partition } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useGraphVisibility } from 'src/app/graph/hooks/useGraphVisibility';

import { Analytics } from '../lib/analytics';
import { ignorePermissionsErrors } from '../lib/apollo/catchErrors';
import { appLinkContext } from '../lib/apollo/link';
import type {
  AccountIdentityAccountFragment as AccountIdentityAccountFragmentType,
  UI__IdentityQuery,
  UI__IdentityQueryVariables,
} from '../lib/graphqlTypes/types';

import { useCurrentAccountId } from './useCurrentAccountId';

/**
 * The `me` field of `UI__IdentityQuery`, which may be a `User` or one of the
 * machine identity types, but is known to be present.
 */
type Identity = NonNullable<UI__IdentityQuery['me']>;

/** The `me` field of `UI__IdentityQuery`, narrowed to a human `User`. */
export type UserIdentity = Extract<
  UI__IdentityQuery['me'],
  { __typename: 'User' }
>;

const UserIdentityAnalyticsFragment = gql`
  fragment UserIdentityAnalyticsFragment on User {
    id
    fullName
    email
    avatarUrl
    createdAt
  }
`;

const AccountIdentityAccountFragment = gql`
  fragment AccountIdentityAccountFragment on Account {
    id
    internalID
    name
    avatarUrl
    createdAt
    isLocked
    currentPlan {
      id
      tier
    }
  }
`;

export const identityQuery = gql<
  UI__IdentityQuery,
  UI__IdentityQueryVariables
>/* GraphQL */ `
  query UI__IdentityQuery($accountId: ID) {
    isLoggedIn @client
    ssoLoginUrl(accountId: $accountId)
    me {
      id
      ... on User {
        id
        email
        emailVerified
        fullName
        avatarUrl
        internalAdminRole
        canUpdateEmail
        canUpdateFullName
        acceptedPrivacyPolicyAt
        type
        logoutAfterIdleMs
        memberships {
          role: permission
          account {
            id
            ...AccountIdentityAccountFragment
          }
          user {
            id
          }
        }
        ...UserIdentityAnalyticsFragment
      }
    }
  }
  ${UserIdentityAnalyticsFragment}
  ${AccountIdentityAccountFragment}
`;

export const chooseDefaultAccountId = (me: Identity) => {
  // Don't default an org to a locked account unless there are no unlocked account
  if (me.__typename === 'User') {
    const [unlockedOrgs = [], lockedOrgs = []] = partition(
      me.memberships
        .map(({ account }) => account)
        .sort((a, b) => (a.name > b.name ? 1 : -1)),
      ['isLocked', false],
    );
    return (
      (unlockedOrgs.length
        ? unlockedOrgs[0]?.id
        : lockedOrgs.length
          ? lockedOrgs[0]?.id
          : null) || null
    );
  }

  return null;
};

const findOwnAccount = (
  me: Identity,
  accountId: string,
): AccountIdentityAccountFragmentType | null => {
  let account = null;

  if (me.__typename === 'User') {
    const memberships: UserIdentity['memberships'] = me.memberships;
    account =
      memberships.find((membership) => membership.account.id === accountId)
        ?.account || null;
  }

  return account;
};

export const isUserIdentity = (user: {
  __typename: string;
}): user is UserIdentity => user.__typename === 'User';

// we keep track of `lastSeenUserId` at file scope to prevent re-running analytics side-effects
// in the `useIdentity` hook.
let lastSeenUserId: string | null = null;

/**
 * useIdentity() is a React Hook that provides the user's current identity,
 * which is comprised of 4 things:
 * `me`: The "me" value from a GraphQL query which describes a user.
 * `meLoading`: true if the me query is still loading.
 * This hook has several side-effects:
 * - setting Apollo local state `currentAccountId` to a reasonable default if it is not yet set,
 * - setting Analytics context for the current user / account
 * - setting the Sentry context for the current user.
 */
type IdentityHook = {
  me: UserIdentity | null;
  isUser: boolean;
  meLoading: boolean;
  accountId: string | null;
  myAccount: AccountIdentityAccountFragmentType | null;
  error: Error | undefined;
  memberships: UserIdentity['memberships'];
  ssoLoginUrl: string | null;
  refresh: () => void;
  isLoggedIn: boolean;
};

// get from /org/apollo/.../1234567890 path using regex
const getOrgId = (url: string) => {
  const match = url.match(/\/org\/([^/]+)/);
  return match ? match[1] : null;
};
/**
 * @deprecated use useAuth() instead
 *
 * useIdentity() is a React Hook that provides the user's current identity,
 * which is comprised of 4 things:
 * `me`: The "me" value from a GraphQL query which describes a user.
 * `meLoading`: true if the me query is still loading.
 */
export const useIdentity = (): IdentityHook => {
  const location = useLocation();
  const graphVisibility = useGraphVisibility();
  const [currentAccountId, setCurrentAccountId] = useCurrentAccountId();
  // We have an automatic ssoUrl detections and login feature in @RoutedApp.
  // This is for accounts that are on multi-org sso. We use this order of
  // precedence to ensure that we always have the correct intended accountId.
  const accountId = getOrgId(location.pathname) || currentAccountId;

  const {
    data: { me, ssoLoginUrl, isLoggedIn = false } = {} as UI__IdentityQuery,
    loading: meLoading,
    error,
    refetch,
  } = useQuery(identityQuery, {
    variables: { accountId },
    errorPolicy: 'all',
    context: appLinkContext({
      catchErrors: [
        ignorePermissionsErrors,
        (err) => {
          // The server is telling us of an error that can be fixed by logging
          // in through a web browser. We can ignore this error since we later
          // redirect to the login page for the "logged-in: false" header.
          return err.extensions?.needsWebLogin === true;
        },
      ],
    }),
  });

  const activeAccountId = useMemo(() => {
    if (currentAccountId) return currentAccountId;
    if (me && graphVisibility !== 'public') {
      return chooseDefaultAccountId(me);
    }
  }, [currentAccountId, graphVisibility, me]);

  useEffect(() => {
    if (currentAccountId !== activeAccountId && activeAccountId) {
      setCurrentAccountId(activeAccountId);
    }
  }, [activeAccountId, currentAccountId, setCurrentAccountId]);

  const myAccount =
    (me && activeAccountId && findOwnAccount(me, activeAccountId)) || null;

  useEffect(() => {
    if (me && me.id !== lastSeenUserId) {
      lastSeenUserId = me.id;
      if (isUserIdentity(me)) {
        if (Analytics.isAvailable()) {
          Analytics.identify(me);
          if (myAccount) Analytics.group(myAccount);
        }
        // datadog RUM user context
        datadogRum.setUser({
          id: me.id,
          email: me.email || undefined,
          name: me.fullName,
        });
        if (myAccount) {
          datadogRum.setAccount({
            id: myAccount.id,
            name: myAccount?.name,
          });
        }
      }
    }
  }, [me, myAccount]);

  const isUser = me?.__typename === 'User';
  const identity = me as UserIdentity;

  return {
    me: identity,
    memberships: isUser ? identity.memberships : [],
    accountId,
    myAccount,
    ssoLoginUrl,
    isUser,
    isLoggedIn: isLoggedIn || false,
    meLoading,
    error,
    refresh: refetch,
  };
};
